// src/components/lists/BookingsList/BookingsList.tsx

import React from 'react';
import { Typography, Chip, ChipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Event as EventIcon,
  CalendarToday as CalendarTodayIcon,
  WarningAmberOutlined as WarningIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { colors } from '../../../theme/theme';
import CopyButton from '../../common/CopyButton';
import { getCustomIcon } from '../../../utils/customIcons';
import {
  ListContainer,
  ListRow,
  RowClickableArea,
  ItemNameSection,
  ItemIcon,
  ItemName,
  ActionButtons,
} from '../base/ListStyles';
import {
  ReservationsSection,
  ReservationsList,
  ReservationItem,
  PetCountDisplay,
  ReservationDate,
  ContactDetailsSection,
  ContactSection,
  ContactItem,
} from '../BeingsList/beingsListStyles';
import { useListState, useListUtils } from '../base/ListBase';
import { BookingsListProps } from './bookingsListTypes';
import {
  formatBookingDateRange,
  formatBookingDateRangeString,
  formatBookingPetTypeCounts,
  formatQuoteExpiry,
} from './bookingsListUtils';
import {
  BeingsListPrimaryButton,
  BeingsListSecondaryButton,
  BeingsListTertiaryButton,
} from '../../common/Buttons';
import { usePermissions } from '../../../hooks/usePermissions';

const EnhancedListRow = styled(ListRow)<{ isOpen?: boolean; isEven?: boolean }>`
  &:hover {
    .pet-count-display {
      opacity: ${(props) => (props.isOpen ? 1 : 0.8)} !important;
    }
  }
`;

const PetColumnsWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: '0.8rem',
});

const PetIconColumn = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  '@media (max-width: 600px)': { paddingLeft: '1rem' },
});

const PetNameColumn = styled('div')({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const ProspectChip = styled((props: ChipProps) => (
  <Chip component="span" {...props} />
))(({ theme }) => ({
  marginLeft: '0.5rem',
  height: '1.6rem',
  fontSize: '0.95rem',
  letterSpacing: '0.05em',
  padding: '1.5rem 0.25rem',
  backgroundColor: theme.palette.tertiary?.main ?? theme.palette.info.main,
  color:
    theme.palette.tertiary?.contrastText ?? theme.palette.info.contrastText,
  '& .MuiChip-label': {
    fontSize: '1.5rem',
    overflow: 'visible',
  },
}));

// Shown on retained quotes that have already been converted to a booking.
// Uses the success palette so it reads as "this quote produced a booking",
// distinct from the secondary-coloured Quote chip and tertiary Prospect chip.
const ConvertedChip = styled((props: ChipProps) => (
  <Chip component="span" {...props} />
))(({ theme }) => ({
  marginLeft: '0.5rem',
  height: '1.6rem',
  letterSpacing: '0.05em',
  backgroundColor: theme.palette.success.main,
  color: theme.palette.success.contrastText,
  padding: '1.5rem 0.25rem',
  '& .MuiChip-label': {
    fontSize: '1.5rem',
    overflow: 'visible',
  },
}));

// Shown on quotes whose QuoteExpiresAt has passed and haven't been converted.
const ExpiredChip = styled((props: ChipProps) => (
  <Chip component="span" {...props} />
))(({ theme }) => ({
  marginLeft: '0.5rem',
  height: '1.6rem',
  letterSpacing: '0.05em',
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  padding: '1.5rem 0.25rem',
  '& .MuiChip-label': {
    fontSize: '1.5rem',
    overflow: 'visible',
  },
}));

const PetActionColumn = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  paddingRight: '0.8rem',
});

const PetViewLink = styled(Typography)({
  cursor: 'pointer',
  color: colors.primary.main,
  fontWeight: 500,
  fontSize: '1.3rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  paddingRight: '0.6rem',
  userSelect: 'none',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    textDecoration: 'underline',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
  },
  '&:focus-visible': {
    outline: `2px solid ${colors.primary.main}`,
    outlineOffset: '2px',
  },
});

const BookingsList: React.FC<BookingsListProps> = ({
  items,
  onEdit,
  onView,
  onDelete,
  onViewDetails,
  onCancel,
  openItemId: openItemIdProp,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const { canEditBookings } = usePermissions();

  // Controlled when openItemIdProp is defined (number | null);
  // uncontrolled when undefined (fall back to internal state).
  const isControlled = openItemIdProp !== undefined;
  const internal = useListState();
  const openItemId = isControlled
    ? (openItemIdProp ?? null)
    : internal.openItemId;

  const setOpenItemId = (id: number | null) => {
    if (isControlled) {
      onOpenChange?.(id);
    } else {
      internal.setOpenItemId(id);
      onOpenChange?.(id);
    }
  };

  const { handleRowClick, handleNameClick } = useListUtils(
    openItemId,
    setOpenItemId
  );

  const navigateToPet = (petId: number, e: React.SyntheticEvent) => {
    e.stopPropagation();
    navigate(`/pets/${petId}`);
  };

  return (
    <ListContainer>
      {items.map((booking, index) => {
        const isQuote = booking.status === 'Quotation';
        const isOpen = openItemId === booking.id;
        const isEven = index % 2 === 0;
        const dateRange = formatBookingDateRange(booking);
        const dateRangeString = formatBookingDateRangeString(booking);
        const petTypeCounts = formatBookingPetTypeCounts(booking);
        const bookingLabel = `${booking.clientSurname} - ${dateRangeString} - Booking`;

        return (
          <EnhancedListRow
            key={booking.id}
            id={`booking-row-${booking.id}`}
            isOpen={isOpen}
            isEven={isEven}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            aria-label={bookingLabel}
            onClick={() => handleRowClick(booking.id)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleRowClick(booking.id);
              }
            }}
          >
            <RowClickableArea
              isOpen={isOpen}
              onClick={() => handleRowClick(booking.id)}
            />

            <ItemNameSection
              isOpen={isOpen}
              onClick={
                isOpen
                  ? (e) => handleNameClick(booking.id, e)
                  : (e) => {
                      e.stopPropagation();
                      handleRowClick(booking.id);
                    }
              }
              role="button"
              aria-label={bookingLabel}
              tabIndex={0}
            >
              <ItemIcon isOpen={isOpen}>
                {isQuote ? getCustomIcon('quotation', '2.2rem') : <EventIcon />}
              </ItemIcon>
              <ItemName
                isOpen={isOpen}
                sx={isQuote ? { fontStyle: 'italic' } : undefined}
              >
                <span className="bookingSurname">{booking.clientSurname}</span>,{' '}
                <span className="bookingFirstname">{booking.clientName}</span>,{' '}
                <span className="bookingDate">
                  (<span className="bookingDateBold">{dateRange}</span>)
                </span>
                {isQuote && booking.clientIsProspect && (
                  <ProspectChip label="Prospect" size="small" />
                )}
                {isQuote &&
                  booking.quoteExpiresAt &&
                  booking.convertedToBookingId == null &&
                  formatQuoteExpiry(booking.quoteExpiresAt).isExpired && (
                    <ExpiredChip label="Expired" size="small" />
                  )}
                {isQuote && booking.convertedToBookingId != null && (
                  <>
                    <ConvertedChip label="Converted" size="small" />
                  </>
                )}
                <PetCountDisplay isOpen={isOpen} className="pet-count-display">
                  {petTypeCounts}
                </PetCountDisplay>
              </ItemName>
            </ItemNameSection>

            {isOpen && (
              <ActionButtons>
                <BeingsListPrimaryButton
                  size="small"
                  onClick={() => onView!(booking.id)}
                >
                  View
                </BeingsListPrimaryButton>
                {canEditBookings && (
                  <BeingsListSecondaryButton
                    size="small"
                    disabled={booking.convertedToBookingId != null}
                    onClick={() => onEdit!(booking.id)}
                  >
                    Edit
                  </BeingsListSecondaryButton>
                )}
                {!(isQuote && booking.clientIsProspect) && (
                  <BeingsListTertiaryButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/clients/${booking.clientId}`);
                    }}
                  >
                    Client Details
                  </BeingsListTertiaryButton>
                )}
              </ActionButtons>
            )}

            <ReservationsSection isOpen={isOpen}>
              <ReservationsList>
                {booking.pets && booking.pets.length > 0 ? (
                  booking.pets.map((pet, idx) => (
                    <ReservationItem key={idx}>
                      <PetColumnsWrapper>
                        <PetIconColumn>
                          {getCustomIcon(pet.petType, '2.2rem')}
                        </PetIconColumn>
                        <PetNameColumn>
                          <ReservationDate>{pet.petName}</ReservationDate>
                        </PetNameColumn>
                        <PetActionColumn>
                          {!pet.isSketch && (
                            <PetViewLink
                              role="button"
                              tabIndex={0}
                              aria-label={`View pet details for ${pet.petName}`}
                              onClick={(e) => navigateToPet(pet.petId, e)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  navigateToPet(pet.petId, e);
                                }
                              }}
                            >
                              View
                            </PetViewLink>
                          )}
                        </PetActionColumn>
                      </PetColumnsWrapper>
                    </ReservationItem>
                  ))
                ) : (
                  <ReservationItem>
                    <ReservationDate>No pets in this booking</ReservationDate>
                  </ReservationItem>
                )}
              </ReservationsList>
            </ReservationsSection>

            <ContactDetailsSection isOpen={isOpen}>
              <ContactSection>
                {booking.clientShowWarning && (
                  <ContactItem>
                    <WarningIcon />
                    <Typography variant="body1" fontWeight={600}>
                      {booking.clientNotes}
                    </Typography>
                  </ContactItem>
                )}

                <ContactItem>
                  <CalendarTodayIcon
                    sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    Created: {dayjs(booking.createdAt).format('D MMM YYYY')}
                  </Typography>
                </ContactItem>

                {isQuote &&
                  booking.quoteExpiresAt &&
                  booking.convertedToBookingId == null &&
                  (() => {
                    const expiry = formatQuoteExpiry(booking.quoteExpiresAt);
                    return (
                      <ContactItem>
                        <CalendarTodayIcon
                          sx={{
                            fontSize: '1.4rem',
                            color: expiry.isExpired
                              ? colors.error.main
                              : colors.primary.main,
                          }}
                        />
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={
                            expiry.isExpired
                              ? { color: colors.error.main }
                              : undefined
                          }
                        >
                          {expiry.label}
                        </Typography>
                      </ContactItem>
                    );
                  })()}

                {booking.clientPhone && (
                  <ContactItem>
                    <PhoneIcon
                      sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                    />
                    <Typography variant="body1" fontWeight={600}>
                      {booking.clientPhone}
                    </Typography>
                    <CopyButton
                      valueToCopy={booking.clientPhone}
                      icon={<CopyIcon fontSize="small" />}
                      size="small"
                      aria-label="Copy phone number"
                    />
                  </ContactItem>
                )}

                {booking.clientEmail && (
                  <ContactItem>
                    <EmailIcon
                      sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                    />
                    <Typography variant="body1" fontWeight={600}>
                      {booking.clientEmail}
                    </Typography>
                    <CopyButton
                      valueToCopy={booking.clientEmail}
                      icon={<CopyIcon fontSize="small" />}
                      size="small"
                      aria-label="Copy email address"
                    />
                  </ContactItem>
                )}
              </ContactSection>
            </ContactDetailsSection>
          </EnhancedListRow>
        );
      })}
    </ListContainer>
  );
};

export default BookingsList;
